/**
 * StatusPill — the ONE status vocabulary, rendered identically everywhere
 * (picker, strip, cards, guards). P2 spine row governs.
 *
 *  - Three states, ALWAYS words + a non-color glyph (✓ / ! / ○ — color is
 *    never the sole carrier), sentence-case (our UI sans ships no true small
 *    caps, so we never synthesize them).
 *  - Chip template: `[State] · [Object]` — e.g. "Connected · Google Ads".
 *  - Stable footprint: the box class is IDENTICAL across states
 *    (STATUS_PILL_BOX_CLASS); only the token pair and glyph change.
 *  - Stale/failure is a MODIFIER on the three states, never a fourth state:
 *    `staleSuffix` renders the pill at reduced emphasis plus the suffix text
 *    ("Can't reach — retrying" when verification failed outright,
 *    "Updated Nm ago" when we're showing last-known data). Rendered, never
 *    silently presenting last-known as current.
 *  - No spinners anywhere (sub-400 ms responses need no progress affordance).
 *
 * Tokens only: named @theme classes — no hex, no raw palette utilities.
 */

import { cn } from '@/lib/utils';
import type { CapabilityState } from '@/hooks/useClientCapabilities';

/** The identical box class across all three states (stable footprint). */
export const STATUS_PILL_BOX_CLASS =
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium';

const STATE_TOKENS: Record<CapabilityState, string> = {
  connected: 'bg-teal-light text-teal-dark',
  action_needed: 'bg-amber-light text-amber-dark',
  not_set_up: 'bg-paper-2 text-ink-3',
};

const STATE_LABEL: Record<CapabilityState, string> = {
  connected: 'Connected',
  action_needed: 'Action needed',
  not_set_up: 'Not set up',
};

/** The state vocabulary — the single source, re-exported via copyTokens. */
export const STATUS_PILL_STATE_LABEL = STATE_LABEL;

const STATE_GLYPH: Record<CapabilityState, string> = {
  connected: '✓',
  action_needed: '!',
  not_set_up: '○',
};

export interface StatusPillProps {
  state: CapabilityState;
  /** The object half of the template, e.g. "Google Ads". Omit for state-only (cards). */
  object?: string;
  /**
   * Stale/failure modifier suffix (from staleSuffixFor). When present the
   * pill renders at reduced emphasis with the suffix appended — a modifier
   * on the state, not a state of its own.
   */
  staleSuffix?: string;
  /**
   * Whether the state's claim is backed by persisted verification. Pass the
   * capability's `verified` flag. When `false` and the state is 'connected',
   * the pill AUTOMATICALLY carries the "Not verified" modifier — a bare
   * "Connected" can never be rendered from an unverified claim (WP-honesty
   * rule: key presence is not verification).
   */
  verified?: boolean;
  className?: string;
}

/** Suffix for an unverified 'connected' claim (WP-honesty rule). */
export const NOT_VERIFIED_SUFFIX = 'Not verified';

export function StatusPill({ state, object, staleSuffix, verified, className }: StatusPillProps) {
  // The stale/failure modifier wins over the verification modifier (data
  // currency is the more urgent honesty signal); both are modifiers at
  // reduced emphasis, never states of their own.
  const modifierSuffix =
    staleSuffix ?? (state === 'connected' && verified === false ? NOT_VERIFIED_SUFFIX : undefined);
  return (
    <span
      className={cn(
        STATUS_PILL_BOX_CLASS,
        STATE_TOKENS[state],
        modifierSuffix && 'opacity-75',
        className,
      )}
    >
      {/* Glyph is decorative — the words always carry the state. */}
      <span aria-hidden="true" className="w-3 text-center leading-none">
        {STATE_GLYPH[state]}
      </span>
      <span>
        {STATE_LABEL[state]}{object ? ` · ${object}` : ''}
      </span>
      {modifierSuffix ? <span className="text-ink-3">· {modifierSuffix}</span> : null}
    </span>
  );
}

/**
 * Compute the stale/failure modifier suffix for a capabilities consumer.
 *  - Verification failed with no usable data → "Can't reach — retrying".
 *  - Verification failed WITH last-known data → "Updated Nm ago" (N = whole
 *    minutes since the last successful fetch; "just now" rounds to 0m).
 *  - Verified → no modifier.
 */
export function staleSuffixFor({
  isError,
  dataUpdatedAt,
  now = Date.now(),
}: {
  isError: boolean;
  dataUpdatedAt?: number;
  now?: number;
}): string | undefined {
  if (!isError) return undefined;
  if (!dataUpdatedAt) return "Can't reach — retrying";
  const minutes = Math.max(0, Math.round((now - dataUpdatedAt) / 60_000));
  return `Updated ${minutes}m ago`;
}
